import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChefHat, Trophy, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { NAV, PROFILE } from "@/constants/testIds";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  published: "bg-emerald-100 text-emerald-900 border-emerald-300",
  rejected: "bg-rose-100 text-rose-900 border-rose-300",
};

export default function ProfilePage() {
  const { user, setUser, loading } = useAuth();
  const { lang } = useLang();
  const t = useDict(lang);
  const navigate = useNavigate();
  const [tab, setTab] = useState("recipes");
  const [recipes, setRecipes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [gam, setGam] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.myRecipes().then(setRecipes).catch(() => {});
    api.myOrders().then(setOrders).catch(() => {});
    api.myGamification().then(setGam).catch(() => {});
  }, [user]);

  if (!user) return null;

  const logout = async () => {
    try { await api.logout(); } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("Logout failed:", e?.message);
    }
    setUser(null);
    navigate("/");
  };

  const tierKey = gam?.tier?.key || "goûteur";
  const points = gam?.points_total || 0;
  const nextThreshold = gam?.tier?.next_threshold;
  const progress = nextThreshold
    ? Math.min(100, Math.round(((points - (gam.tier.threshold || 0)) / (nextThreshold - (gam.tier.threshold || 0))) * 100))
    : 100;

  return (
    <div data-testid={PROFILE.page} className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16">
      <p className="eyebrow mb-4">№ 05</p>
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="font-serif-display text-5xl sm:text-6xl">{user.name || user.email}</h1>
          <p className="text-[color:var(--ink-2)] mt-2">{user.email}</p>
        </div>
        <button data-testid={NAV.logout} onClick={logout} className="btn-outline">{t.profile.logout}</button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-12">
        <div data-testid="profile-points-card" className="border border-[color:rgba(0,16,48,0.12)] p-5 bg-white">
          <p className="eyebrow flex items-center gap-2 mb-2"><ChefHat size={14} color="var(--red)" /> {t.profile.tierLabel}</p>
          <p className="font-serif-display text-2xl leading-tight">{t.tier[tierKey] || tierKey}</p>
          <p className="text-sm text-[color:var(--ink-2)] mt-1">{points} pts</p>
          {nextThreshold && (
            <>
              <div className="mt-3 h-1.5 bg-[var(--bg-2)] overflow-hidden">
                <div className="h-full bg-[color:var(--red)] transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)] mt-2">
                {nextThreshold - points} pts {t.profile.progressTo} {t.tier[gam.tier.next_key] || gam.tier.next_key}
              </p>
            </>
          )}
        </div>
        <Link
          to="/leaderboard"
          data-testid="profile-leaderboard-cta"
          className="border border-[color:rgba(0,16,48,0.12)] p-5 bg-white hover:border-[color:var(--ink)] transition-colors group block"
        >
          <p className="eyebrow flex items-center gap-2 mb-2"><Trophy size={14} color="var(--red)" /> {t.leaderboard.title}</p>
          <p className="font-serif-display text-2xl leading-tight">{t.leaderboard.cta}</p>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--ink-3)] mt-3 group-hover:text-[color:var(--ink)]">{t.leaderboard.pageTitle} →</p>
        </Link>
        <Link
          to="/referral"
          data-testid="profile-referral-cta"
          className="border border-[color:rgba(0,16,48,0.12)] p-5 bg-white hover:border-[color:var(--ink)] transition-colors group block"
        >
          <p className="eyebrow flex items-center gap-2 mb-2"><Gift size={14} color="var(--red)" /> {t.profile.referralCta}</p>
          <p className="font-serif-display text-2xl leading-tight">{user.referral_code || "—"}</p>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--ink-3)] mt-3 group-hover:text-[color:var(--ink)]">{t.referral.title} →</p>
        </Link>
      </div>

      <div className="flex gap-8 border-b border-[color:rgba(0,16,48,0.15)] mb-10">
        <button data-testid={PROFILE.recipesTab} onClick={() => setTab("recipes")} className={`pb-4 text-xs tracking-[0.22em] uppercase font-medium ${tab==='recipes'?'border-b-2 border-[color:var(--red)]':''}`}>{t.profile.myRecipes}</button>
        <button data-testid={PROFILE.ordersTab} onClick={() => setTab("orders")} className={`pb-4 text-xs tracking-[0.22em] uppercase font-medium ${tab==='orders'?'border-b-2 border-[color:var(--red)]':''}`}>{t.profile.myOrders}</button>
      </div>

      {tab === "recipes" ? (
        recipes.length === 0 ? <p className="text-[color:var(--ink-3)] italic font-serif-display text-2xl">{t.profile.noRecipes}</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {recipes.map((r) => {
              const status = r.status || "published";
              const canEdit = status === "pending" || status === "rejected";
              const cardInner = (
                <>
                  <div className="recipe-card-img"><img src={r.image} alt="" /></div>
                  <div className="flex items-center justify-between gap-2 mt-3">
                    <p className="eyebrow">{r.country}</p>
                    <span
                      data-testid={PROFILE.statusBadge(r.slug)}
                      className={`text-[10px] tracking-[0.18em] uppercase font-medium px-2 py-1 border ${STATUS_STYLES[status] || STATUS_STYLES.published}`}
                    >
                      {t.profile.status[status] || status}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-xl mt-1">{r.title[lang] || r.title.fr}</h3>
                  {status === "rejected" && r.rejection_reason && (
                    <p className="text-xs text-[color:var(--ink-3)] mt-2 italic">{t.profile.rejectionReason}: {r.rejection_reason}</p>
                  )}
                </>
              );
              return (
                <div key={r.recipe_id} className="recipe-card flex flex-col">
                  {status === "published" ? (
                    <Link to={`/recipes/${r.slug}`} className="contents">{cardInner}</Link>
                  ) : (
                    <div className="contents">{cardInner}</div>
                  )}
                  {canEdit && (
                    <Link
                      to={`/submit/${r.recipe_id}`}
                      data-testid={PROFILE.editRecipe(r.slug)}
                      className="btn-outline mt-4 self-start text-xs"
                    >
                      {t.profile.edit}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        orders.length === 0 ? <p className="text-[color:var(--ink-3)] italic font-serif-display text-2xl">{t.profile.noOrders}</p> : (
          <ul className="space-y-4">
            {orders.map((o) => (
              <li key={o.order_id} className="border border-[color:rgba(0,16,48,0.15)] p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="eyebrow">{o.order_id}</p>
                  <p className="font-serif-display text-xl mt-1">{o.items.length} item(s)</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--ink-3)]">{o.status}</p>
                  <p className="font-serif-display text-2xl">{o.total.toFixed(2)} €</p>
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
